import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { AuthService } from 'src/app/service/auth.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Mensagem {
  id?: number;
  titulo: string;
  horario: string;
  mensagem: string;
  ativo: boolean;
}

interface DiaSemana {
  id: number;
  nome: string;
  mensagens: Mensagem[];
}

@Component({
  selector: 'app-mensagens-diarias',
  templateUrl: './mensagens-diarias.component.html',
  styleUrls: ['./mensagens-diarias.component.scss']
})
export class MensagensDiariasComponent implements OnInit, OnDestroy {

  public diasSemana: DiaSemana[] = [
    { id: 0, nome: 'Domingo', mensagens: [] },
    { id: 1, nome: 'Segunda-feira', mensagens: [] },
    { id: 2, nome: 'Terça-feira', mensagens: [] },
    { id: 3, nome: 'Quarta-feira', mensagens: [] },
    { id: 4, nome: 'Quinta-feira', mensagens: [] },
    { id: 5, nome: 'Sexta-feira', mensagens: [] },
    { id: 6, nome: 'Sábado', mensagens: [] }
  ];

  public diaSelecionado: number;
  public mensagemEditando: number = null;
  public modalAberto: boolean = false;
  public novaMensagem: Mensagem = {
    titulo: '',
    horario: '',
    mensagem: '',
    ativo: true
  };

  constructor(
    private router: Router,
    private ajax: AjaxService,
    public util: UtilService,
    private auth: AuthService
  ) {
  }

  ngOnInit() {
    this.carregarMensagens();
  }

  ngOnDestroy(): void {
  }

  carregarMensagens() {
    this.util.showLoading();
    let url = API + "NotificacaoDiaria/NotificacaoDiaria";
    
    this.ajax.get(url).subscribe(
      response => {
        if (response.data && Array.isArray(response.data)) {
          // Organizar mensagens por dia da semana
          response.data.forEach((item: any) => {
            const dia = this.diasSemana.find(d => d.nome === item.diA_SEMANA);
            if (dia) {
              dia.mensagens.push({
                id: item.iD_NOTIFICACOES_DIARIAS,
                titulo: item.titulo || 'Mensagem',
                horario: this.converterTimeSpanParaHorario(item.horA_ENVIO),
                mensagem: item.mensagem,
                ativo: item.ativo
              });
            }
          });
        }
        this.util.closeLoading();
      },
      err => {
        this.util.closeLoading();
        console.log('Erro ao carregar mensagens:', err);
        // Inicializar com dados vazios
      }
    );
  }

  getMensagensCount(diaId: number): number {
    const dia = this.diasSemana.find(d => d.id === diaId);
    return dia ? dia.mensagens.length : 0;
  }

  getMensagensDia(diaId: number): Mensagem[] {
    const dia = this.diasSemana.find(d => d.id === diaId);
    return dia ? dia.mensagens : [];
  }

  getDiaNome(diaId: number): string {
    const dia = this.diasSemana.find(d => d.id === diaId);
    return dia ? dia.nome : '';
  }

  converterHorarioParaTimeSpan(horario: string): any {
    if (!horario || horario === '') {
      return '00:00:00';
    }
    
    // Se já está no formato HH:mm, adiciona os segundos
    if (horario.length === 5 && horario.includes(':')) {
      return horario + ':00';
    }
    
    // Se está no formato HH:mm:ss, retorna como está
    if (horario.length === 8 && horario.split(':').length === 3) {
      return horario;
    }
    
    // Fallback para formato padrão
    return '00:00:00';
  }

  // Método alternativo para tentar diferentes formatos
  converterHorarioParaTimeSpanAlternativo(horario: string): any {
    if (!horario || horario === '') {
      return '00:00:00';
    }
    
    // Tentar formato ISO 8601 duration
    const [hora, minuto] = horario.split(':');
    const totalSegundos = (parseInt(hora) * 3600) + (parseInt(minuto) * 60);
    
    // Retornar como objeto TimeSpan do .NET
    return {
      ticks: totalSegundos * 10000000, // 1 segundo = 10,000,000 ticks
      days: 0,
      hours: parseInt(hora),
      milliseconds: 0,
      minutes: parseInt(minuto),
      seconds: 0,
      totalDays: totalSegundos / 86400,
      totalHours: parseInt(hora),
      totalMilliseconds: totalSegundos * 1000,
      totalMinutes: totalSegundos / 60,
      totalSeconds: totalSegundos
    };
  }

  // Converter para formato ISO 8601 duration
  converterParaISO8601(horario: string): string {
    if (!horario || horario === '') {
      return 'PT00H00M';
    }
    
    const [hora, minuto] = horario.split(':');
    return `PT${hora.padStart(2, '0')}H${minuto.padStart(2, '0')}M`;
  }

  // Método que tenta diferentes formatos para TimeSpan
  tentarFormatosTimeSpan(horario: string): any {
    if (!horario || horario === '') {
      return '00:00:00';
    }

    // Opção 1: Formato HH:mm:ss simples
    const formato1 = horario.length === 5 ? horario + ':00' : horario;
    
    // Opção 2: ISO 8601 duration
    const [hora, minuto] = horario.split(':');
    const formato2 = `PT${hora.padStart(2, '0')}H${minuto.padStart(2, '0')}M`;
    
    // Opção 3: Com prefixo T
    const formato3 = `T${formato1}`;
    
    // Opção 4: Formato com ticks (total de segundos)
    const totalSegundos = (parseInt(hora) * 3600) + (parseInt(minuto) * 60);
    
    console.log('Formatos tentados:');
    console.log('Formato 1 (HH:mm:ss):', formato1);
    console.log('Formato 2 (ISO 8601):', formato2);
    console.log('Formato 3 (T prefix):', formato3);
    console.log('Total segundos:', totalSegundos);
    
    // Retornar o formato simples primeiro
    return formato1;
  }

  converterTimeSpanParaHorario(timeSpan: string): string {
    if (!timeSpan || timeSpan === '') {
      return '00:00';
    }
    
    // Se está no formato HH:mm:ss, remove os segundos
    if (timeSpan.length === 8 && timeSpan.split(':').length === 3) {
      return timeSpan.substring(0, 5);
    }
    
    // Se já está no formato HH:mm, retorna como está
    if (timeSpan.length === 5 && timeSpan.includes(':')) {
      return timeSpan;
    }
    
    // Fallback para formato padrão
    return '00:00';
  }

  abrirModalMensagem(diaId: number) {
    this.diaSelecionado = diaId;
    this.mensagemEditando = null;
    this.novaMensagem = {
      titulo: '',
      horario: '',
      mensagem: '',
      ativo: true
    };
    this.modalAberto = true;
  }

  editarMensagem(diaId: number, index: number) {
    this.diaSelecionado = diaId;
    this.mensagemEditando = index;
    const dia = this.diasSemana.find(d => d.id === diaId);
    if (dia && dia.mensagens[index]) {
      this.novaMensagem = { 
        id: dia.mensagens[index].id,
        titulo: dia.mensagens[index].titulo,
        horario: dia.mensagens[index].horario,
        mensagem: dia.mensagens[index].mensagem,
        ativo: dia.mensagens[index].ativo
      };
    }
    this.modalAberto = true;
  }

  removerMensagem(diaId: number, index: number) {
    if (confirm('Deseja realmente remover esta mensagem?')) {
      const dia = this.diasSemana.find(d => d.id === diaId);
      if (dia && dia.mensagens[index]) {
        const mensagem = dia.mensagens[index];
        
        // Se a mensagem tem ID, fazer chamada para API
        if (mensagem.id) {
          this.util.showLoading();
          
          const dados = {
            iD_NOTIFICACOES_DIARIAS: mensagem.id
          };
          
          const url = API + "NotificacaoDiaria/NotificacaoDiaria/Desabilitar";
          
          this.ajax.post(url, dados).subscribe(
            response => {
              this.util.closeLoading();
              
              // Remover localmente após sucesso na API
              dia.mensagens.splice(index, 1);
              this.util.toasterShowSuccess('Sucesso', 'Mensagem removida com sucesso!');
            },
            err => {
              this.util.closeLoading();
              this.util.toasterShowError('Erro', 'Erro ao remover mensagem. Tente novamente.');
              console.log('Erro ao remover mensagem:', err);
            }
          );
        } else {
          // Se não tem ID, remover apenas localmente
          dia.mensagens.splice(index, 1);
          this.util.toasterShowSuccess('Sucesso', 'Mensagem removida com sucesso!');
        }
      }
    }
  }

  fecharModal() {
    this.modalAberto = false;
    this.limparFormulario();
  }

  limparFormulario() {
    this.novaMensagem = {
      id: undefined,
      titulo: '',
      horario: '',
      mensagem: '',
      ativo: true
    };
    this.mensagemEditando = null;
  }

  validarMensagem(): boolean {
    if (!this.novaMensagem.titulo || this.novaMensagem.titulo.trim() === '') {
      this.util.toasterShowError('Atenção', 'Informe o título da mensagem');
      return false;
    }

    if (!this.novaMensagem.horario || this.novaMensagem.horario === '') {
      this.util.toasterShowError('Atenção', 'Informe o horário de envio');
      return false;
    }

    if (!this.novaMensagem.mensagem || this.novaMensagem.mensagem.trim() === '') {
      this.util.toasterShowError('Atenção', 'Digite a mensagem');
      return false;
    }

    return true;
  }

  salvarMensagem() {
    if (!this.validarMensagem()) {
      return;
    }

    this.util.showLoading();

    // Preparar dados para a API - tentar formato simples HH:mm:ss
    const dados: any = {
      titulo: this.novaMensagem.titulo,
      mensagem: this.novaMensagem.mensagem,
      horA_ENVIO: this.tentarFormatosTimeSpan(this.novaMensagem.horario),
      diA_SEMANA: this.getDiaNome(this.diaSelecionado),
      ativo: this.novaMensagem.ativo,
      datA_CRIACAO: new Date().toISOString()
    };

    // Se estiver editando, incluir o ID
    if (this.mensagemEditando !== null && this.novaMensagem.id) {
      dados.iD_NOTIFICACOES_DIARIAS = this.novaMensagem.id;
    }

    console.log('Dados sendo enviados:', dados);
    console.log('Horário original:', this.novaMensagem.horario);
    
    const url = API + "NotificacaoDiaria/NotificacaoDiaria";
    
    this.ajax.post(url, dados).subscribe(
      response => {
        this.util.closeLoading();
        
        // Adicionar mensagem localmente após sucesso na API
        const dia = this.diasSemana.find(d => d.id === this.diaSelecionado);
        if (dia) {
          if (this.mensagemEditando !== null) {
            // Atualizar mensagem existente
            dia.mensagens[this.mensagemEditando] = { ...this.novaMensagem };
            this.util.toasterShowSuccess('Sucesso', 'Mensagem atualizada com sucesso!');
          } else {
            // Adicionar nova mensagem
            dia.mensagens.push({ ...this.novaMensagem });
            this.util.toasterShowSuccess('Sucesso', 'Mensagem adicionada com sucesso!');
          }
        }
        
        this.fecharModal();
      },
      err => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar mensagem. Tente novamente.');
        console.log('Erro ao salvar mensagem:', err);
      }
    );
  }


}
